import { useAdCampaignDetail } from 'data/ad-campaign';
import moment from 'moment';
import React from 'react';

import { Chip, Grid, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

import AdPreview from 'components/AdPreview';
import { useAuthDataContext } from 'components/AuthDataProvider/AuthDataProvider';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';

import {
  enumToReadableName, getAgentLandingPageSampleUrlFromType, numberWithCommas,
} from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    fontWeight: 400,

    '& h4': {
      fontWeight: 'bold',
      marginBottom: '4px',
      fontSize: '14px',
      lineHeight: '18px',
      color: theme.palette.black.main,
    },
  },

  infoContainer: {
    marginTop: '20px',
    '& > div': {
      marginBottom: '30px',
    },
  },

  adminInfo: {
    padding: '32px 16px',
    fontWeight: 400,
    fontSize: '14px',
  },

  linkClicks: {
    display: 'flex',
    alignItems: 'center',
    '& a': {
      marginLeft: '5px',
      color: theme.palette.orange.main,
      textDecoration: 'underline',
    },
  },
}));

type Props = {
  adCampaignId: string;
};

export default ({ adCampaignId }: Props) => {
  const classes = useStyles();
  const { isAdmin, isBrokerageAdmin } = useAuthDataContext();

  const { adCampaign, isLoadingAdCampaign } = useAdCampaignDetail(adCampaignId);

  if (isLoadingAdCampaign) {
    return (
      <div className={classes.root}>
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {(isAdmin || isBrokerageAdmin) && (
        <div className={classes.adminInfo}>
          <b>Agent: </b>
          {`${adCampaign.user.firstName} ${adCampaign.user.lastName} (${adCampaign.user._id})`}
        </div>
      )}

      <Grid container justifyContent="flex-start" className={classes.infoContainer}>
        <Grid item xs={12} md={4}>
          <h4>Status</h4>
          {enumToReadableName(adCampaign.status)}
        </Grid>

        <Grid item xs={12} md={4}>
          <h4>Ad Audience</h4>
          {adCampaign.zipCodes.map((zipCode) => (
            <Chip
              key={zipCode}
              // color="primary"
              color="secondary"
              label={`Zip Code ${zipCode}`}
              style={{ margin: '5px', fontWeight: 400 }}
            />
          ))}
          {adCampaign.customAudienceCampaigns.map((campaign) => (
            <Chip
              key={campaign._id}
              color="secondary"
              label={`Campaign ${campaign.name} (${campaign.numSubscribedApns} APNs)`}
              style={{ margin: '5px', fontWeight: 400 }}
            />
          ))}
        </Grid>

        <Grid item xs={12} md={4}>
          <h4>Monthly Budget</h4>
          <div>{`$${numberWithCommas((adCampaign.costs.userMonthlyBudget / 100).toFixed(2))}/mo`}</div>
          <div>{adCampaign.costs.nextInvoiceDate && `Renews on ${moment(adCampaign.costs.nextInvoiceDate).format('MMM D')}`}</div>
        </Grid>

        <Grid item xs={12} md={4}>
          <h4>Reach</h4>
          <Tooltip title="The number of unique accounts that saw your ads at least once." TransitionComponent={React.Fragment}>
            <span>{adCampaign.stats.reach ? numberWithCommas(adCampaign.stats.reach) : 'N/A'}</span>
          </Tooltip>
        </Grid>
        <Grid item xs={12} md={4}>
          <h4>Impressions</h4>
          <Tooltip title="The number of times your ads were on screen." TransitionComponent={React.Fragment}>
            <span>{adCampaign.stats.impressions ? numberWithCommas(adCampaign.stats.impressions) : 'N/A'}</span>
          </Tooltip>
        </Grid>
        <Grid item xs={12} md={4}>
          <h4 className={classes.linkClicks}>
            Link Clicks
            <a
              href={adCampaign.useCustomCallToActionLink ? adCampaign.callToActionLink : getAgentLandingPageSampleUrlFromType(adCampaign.agentLandingPageType!)}
              target="_blank"
              rel="noreferrer"
            >
              <OpenInNewIcon />
            </a>
          </h4>
          <Tooltip title="The number of call-to-action button link clicks." TransitionComponent={React.Fragment}>
            <span>{adCampaign.stats.inlineLinkClicks ? numberWithCommas(adCampaign.stats.inlineLinkClicks) : 'N/A'}</span>
          </Tooltip>
        </Grid>

        <Grid item xs={12} style={{ marginTop: '20px' }}>
          <h4>Ad Preview</h4>
          <AdPreview imageData={adCampaign.latestImagePreviewUrl} />
        </Grid>
      </Grid>
    </div>
  );
};
